// Profils un drošība: vārds, e-pasts (maiņa + apstiprināšana), parole, iziešana citās ierīcēs, konta dzēšana.

import { useState, type FormEvent, type ReactNode } from 'react';
import { fmtStamp, type Me } from '@balansa/shared';
import { api } from '../lib/api';
import { useApp } from '../store';
import { PasswordInput } from './Login';
import { Modal } from './Modal';

function Section(p: { title: string; children: ReactNode; testId?: string }) {
  return (
    <section className="pf-sec" data-testid={p.testId}>
      <div className="cd-sec">{p.title}</div>
      {p.children}
    </section>
  );
}

/** Forma ar vienu iesniegšanu, kļūdas un veiksmes paziņojumu. */
function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const run = (fn: () => Promise<string>) => async (e?: FormEvent) => {
    e?.preventDefault();
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      setMsg({ ok: true, text: await fn() });
    } catch (err) {
      setMsg({ ok: false, text: (err as Error).message });
    }
    setBusy(false);
  };
  const note = msg ? (
    <p className={msg.ok ? 'pf-ok' : 'bad'} role={msg.ok ? 'status' : 'alert'}>
      {msg.text}
    </p>
  ) : null;
  return { busy, run, note };
}

export function ProfileDialog() {
  const A = useApp.getState;
  const me = useApp((s) => s.me)!;
  const auth = useApp((s) => s.auth);
  const close = () => A().closeDialog();
  const min = auth?.minPasswordLength ?? 8;

  const [name, setName] = useState(me.name);
  const nameF = useSubmit();

  const [newEmail, setNewEmail] = useState('');
  const [emailPw, setEmailPw] = useState('');
  const emailF = useSubmit();
  const verifyF = useSubmit();

  const [cur, setCur] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const pwF = useSubmit();

  const sessF = useSubmit();

  const [delPw, setDelPw] = useState('');
  const [delErr, setDelErr] = useState('');

  const apply = (m: Me) => A().setMe(m);

  return (
    <Modal onClose={close} className="wide" label="Profils un drošība" testId="profile-dialog">
      <div className="dlg pf">
        <h3>Profils un drošība</h3>

        <Section title="Profils">
          <form
            className="pf-row"
            onSubmit={nameF.run(async () => {
              apply(await api.patch<Me>('/account', { name: name.trim() }));
              return 'Vārds saglabāts.';
            })}
          >
            <div className="field">
              <label htmlFor="pf-name">Vārds</label>
              <input id="pf-name" className="inp" required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <button type="submit" className="btn sm" disabled={nameF.busy || name.trim() === me.name} data-testid="pf-name-save">
              Saglabāt
            </button>
          </form>
          {nameF.note}
          <div className="pf-email">
            <span>
              E-pasts: <b data-testid="pf-email">{me.email}</b>
            </span>
            {me.emailVerified ? (
              <span className="pf-badge ok">apstiprināts</span>
            ) : (
              <span className="pf-badge">neapstiprināts</span>
            )}
            {!me.emailVerified && auth?.passwordReset === 'email' ? (
              <button
                type="button"
                className="btn sm ghost"
                onClick={() =>
                  void verifyF.run(async () => {
                    await api.post('/account/verify/resend');
                    return `Apstiprināšanas saite nosūtīta uz ${me.email}.`;
                  })()
                }
              >
                Nosūtīt apstiprinājumu
              </button>
            ) : null}
          </div>
          {verifyF.note}
          <p className="cd-hint">Konts izveidots {fmtStamp(me.createdAt)}.</p>
        </Section>

        <Section title="Mainīt e-pastu">
          {me.hasPassword ? (
            <form
              className="pf-grid"
              onSubmit={emailF.run(async () => {
                const r = await api.post<Me & { verificationSent: boolean }>('/account/email', { email: newEmail.trim(), password: emailPw });
                apply(r);
                setNewEmail('');
                setEmailPw('');
                return r.verificationSent ? `E-pasts nomainīts. Uz ${r.email} nosūtījām apstiprināšanas saiti.` : 'E-pasts nomainīts — turpmāk ieej ar jauno adresi.';
              })}
            >
              <div className="field">
                <label htmlFor="pf-newemail">Jaunais e-pasts</label>
                <input id="pf-newemail" className="inp" type="email" required autoComplete="email" maxLength={200} value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
              </div>
              <PasswordInput id="pf-emailpw" label="Pašreizējā parole" value={emailPw} onChange={setEmailPw} autoComplete="current-password" />
              <div>
                <button type="submit" className="btn sm" disabled={emailF.busy} data-testid="pf-email-save">
                  Mainīt e-pastu
                </button>
              </div>
            </form>
          ) : (
            <p className="muted">Lai mainītu e-pastu, vispirms iestati paroli (zemāk).</p>
          )}
          {emailF.note}
        </Section>

        <Section title={me.hasPassword ? 'Mainīt paroli' : 'Iestatīt paroli'} testId="pf-password">
          <form
            className="pf-grid"
            onSubmit={pwF.run(async () => {
              if (pw !== pw2) throw new Error('Jaunās paroles nesakrīt.');
              const r = await api.post<Me & { sessionsEnded: number }>('/account/password', me.hasPassword ? { current: cur, password: pw } : { password: pw });
              apply(r);
              setCur('');
              setPw('');
              setPw2('');
              return r.sessionsEnded
                ? `Parole saglabāta. Izrakstīts ${r.sessionsEnded} ${r.sessionsEnded === 1 ? 'citā ierīcē' : 'citās ierīcēs'}.`
                : 'Parole saglabāta.';
            })}
          >
            {me.hasPassword ? (
              <PasswordInput id="pf-cur" label="Pašreizējā parole" value={cur} onChange={setCur} autoComplete="current-password" />
            ) : (
              <p className="muted" style={{ gridColumn: '1 / -1' }}>
                Kontam vēl nav paroles (ieeja ar Google vai izstrādes režīmā). Iestati to, lai varētu ieiet arī ar e-pastu un paroli.
              </p>
            )}
            <PasswordInput id="pf-new" label={`Jaunā parole (vismaz ${min})`} value={pw} onChange={setPw} autoComplete="new-password" minLength={min} />
            <PasswordInput id="pf-new2" label="Jaunā parole vēlreiz" value={pw2} onChange={setPw2} autoComplete="new-password" minLength={min} />
            <div>
              <button type="submit" className="btn sm primary" disabled={pwF.busy} data-testid="pf-password-save">
                {me.hasPassword ? 'Mainīt paroli' : 'Iestatīt paroli'}
              </button>
            </div>
          </form>
          {pwF.note}
        </Section>

        <Section title="Ierīces un pieteikšanās veidi">
          <ul className="pf-ways">
            <li>
              E-pasts un parole: <b>{me.hasPassword ? 'ieslēgts' : 'nav iestatīta parole'}</b>
            </li>
            <li>
              Google: <b>{me.googleLinked ? 'piesaistīts' : 'nav piesaistīts'}</b>
              {!me.googleLinked && auth?.google ? <span className="cd-hint"> — ieej ar Google, izmantojot to pašu e-pastu, lai piesaistītu</span> : null}
            </li>
          </ul>
          <div className="dlg-row">
            <button
              type="button"
              className="btn sm"
              data-testid="pf-logout-others"
              onClick={() =>
                void sessF.run(async () => {
                  const r = await api.post<{ sessionsEnded: number }>('/account/logout-others');
                  return r.sessionsEnded ? `Izrakstīts ${r.sessionsEnded} ${r.sessionsEnded === 1 ? 'citā ierīcē' : 'citās ierīcēs'}.` : 'Citās ierīcēs neesi pieteicies.';
                })()
              }
            >
              Iziet visās citās ierīcēs
            </button>
          </div>
          {sessF.note}
        </Section>

        <Section title="Konta dzēšana" testId="pf-delete">
          <p className="muted">Tiks neatgriezeniski dzēsts aplis, kārtis, attēli, žurnāls, vēsture un MCP žetoni. Ieteicams pirms tam “Eksportēt”.</p>
          <form
            className="pf-grid"
            onSubmit={async (e) => {
              e.preventDefault();
              setDelErr('');
              const ok = await A().ask({ title: 'Dzēst kontu?', message: 'Šo darbību nevar atsaukt.', ok: 'Dzēst kontu', danger: true });
              if (!ok) return;
              const err = await A().deleteAccount(me.hasPassword ? delPw : undefined);
              if (err) setDelErr(err);
            }}
          >
            {me.hasPassword ? <PasswordInput id="pf-delpw" label="Parole apstiprināšanai" value={delPw} onChange={setDelPw} autoComplete="current-password" /> : null}
            <div>
              <button type="submit" className="btn sm danger" data-testid="pf-delete-account">
                Dzēst kontu…
              </button>
            </div>
          </form>
          {delErr ? <p className="bad">{delErr}</p> : null}
        </Section>

        <div className="dlg-actions">
          <span className="sp" />
          <button type="button" className="btn sm" onClick={close}>
            Aizvērt
          </button>
        </div>
      </div>
    </Modal>
  );
}
